import React, { useState, useEffect, useRef } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import "./ProfilePhotoUpload.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useAuthContext } from "../../hooks/useAuthContext";
import { initializeApp, getApps } from "firebase/app";
import { getAuth } from "firebase/auth";
import firebase from "../../Firebase";
import Cookies from "js-cookie";
import { isInAppBrowser, inAppName, canJumpToBrowser, openInExternalBrowser } from "../../utils/inAppBrowser";

if (!getApps().length) {
  initializeApp(firebase);
}

// The automatic avatar (backend services/avatarService.js) stands in until
// they add a photo — it's not one of their photos, so the grid shows an
// empty slot for it and it doesn't count as having added a photo.
const isAvatarUrl = (url) => typeof url === 'string' && /\/api\/avatar\/(basic\/|v\d+\/)?[A-Za-z0-9]+\.png$/.test(url);
const ownPhotos = (pics) => (Array.isArray(pics) ? pics : []).map(p => (isAvatarUrl(p) ? null : p));

const DND_ITEM_TYPE = "picture";

const DraggablePicture = ({
  picture,
  index,
  movePicture,
  handleFileChange,
  handleDelete,
  status,
  onRetry,
}) => {
  const [, drag] = useDrag(
    () => ({
      type: DND_ITEM_TYPE,
      item: { index },
    }),
    [index]
  );

  const [, drop] = useDrop(
    () => ({
      accept: DND_ITEM_TYPE,
      hover(item) {
        const dragIndex = item.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) {
          return;
        }
        movePicture(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    }),
    [index, movePicture]
  );

  return (
    <div ref={(node) => drag(drop(node))} className="photo-upload-square">
      {picture && status !== 'uploading' && (
        <div className="delete-icon" onClick={() => handleDelete(index)}>
          <FontAwesomeIcon icon={faTimes} />
        </div>
      )}

      {picture ? (
        <>
          <img
            src={picture}
            alt={`Profile ${index + 1}`}
            className="profile-photo-preview"
          />
          {status === 'uploading' && (
            <div className="photo-upload-overlay photo-upload-overlay--uploading">
              <div className="photo-upload-spinner" />
              <span>Uploading…</span>
            </div>
          )}
          {status === 'success' && (
            <div className="photo-upload-overlay photo-upload-overlay--success">
              <span>✓ Saved</span>
            </div>
          )}
          {status === 'error' && (
            <div className="photo-upload-overlay photo-upload-overlay--error" onClick={onRetry}>
              <span>⚠️ Upload failed</span>
              <span className="photo-upload-retry">Tap to retry</span>
            </div>
          )}
        </>
      ) : (
        <>
          {/* The real file input sits invisibly over the "+", so the tap lands
              on it directly. A hidden input opened through its <label> did
              nothing in some in-app browsers (Facebook's, on Android). */}
          <input
            type="file"
            accept="image/*"
            id={`file-input-${index}`}
            className="file-input file-input--overlay"
            onChange={(event) => handleFileChange(index, event)}
            aria-label="Add a photo"
          />
          <label htmlFor={`file-input-${index}`} className="file-label">
            <div className="upload-placeholder">+</div>
          </label>
        </>
      )}
    </div>
  );
};

const ProfilePicturesGrid = ({ setPhotosInfo, setPhotosUploading = () => {} }) => {
  const [pictures, setPictures] = useState(Array(5).fill(null));
  const [uploadTrigger, setUploadTrigger] = useState(0);
  // Per-slot upload state: index -> 'uploading' | 'success' | 'error' | undefined.
  // Kept separate from `pictures` itself so a blob preview and its real upload
  // status are tracked independently.
  const [uploadStatus, setUploadStatus] = useState({});
  // The original File objects, kept only while a slot hasn't successfully
  // uploaded yet, so a failed upload can be retried without re-picking the file.
  const [pendingFiles, setPendingFiles] = useState({});
  const maxPhotos = 5;
  const { user } = useAuthContext();
  const abortControllerRef = useRef(null);
  // Per-slot upload AbortControllers, so an in-flight upload can be cancelled
  // if the component unmounts (belt-and-braces — the wizard should already
  // block navigating away while photosUploading is true).
  const uploadAbortControllersRef = useRef({});

  useEffect(() => {
    const controllers = uploadAbortControllersRef.current;
    return () => {
      Object.values(controllers).forEach(c => c.abort());
    };
  }, []);

  // Aggregate "is anything still uploading" up to the parent, so e.g. the
  // onboarding wizard can disable "Continue" until uploads finish instead of
  // letting the user navigate away mid-upload.
  useEffect(() => {
    setPhotosUploading(Object.values(uploadStatus).some(s => s === 'uploading'));
  }, [uploadStatus, setPhotosUploading]);

  const getFreshTokens = async () => {
    try {
      // Wait for Firebase to restore auth state from IndexedDB if needed
      let currentUser = firebase.auth().currentUser;
      if (!currentUser) {
        currentUser = await new Promise(resolve => {
          const unsub = firebase.auth().onAuthStateChanged(u => { unsub(); resolve(u); });
        });
      }
      if (!currentUser) {
        window.location.href = '/login';
        throw new Error('Not authenticated');
      }
      const idToken = await currentUser.getIdToken(true);
      const csrfToken = Cookies.get("mytoken");
      return { idToken, csrfToken };
    } catch (error) {
      console.error("Token refresh failed:", error);
      throw error;
    }
  };

  const fetchProfilePictures = async () => {
    if (!user?.userId) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const { idToken, csrfToken } = await getFreshTokens();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/userpics`,
        {
          method: "POST",
          credentials: 'include',
          body: JSON.stringify({
            userId: user.userId,
            _csrf: csrfToken,
          }),
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
            "X-CSRF-Token": csrfToken,
          },
          signal: controller.signal,
        }
      );

      if (response.status === 403) {
        await getFreshTokens();
        return fetchProfilePictures();
      }

      if (response.status === 200) {
        const data = await response.json();
        setPictures(ownPhotos(data.pics));
        // Set photosInfo based on whether there's a real (non-blob) photo —
        // the useEffect below re-derives this on every `pictures` change too,
        // this just avoids a flash of the stale value before that runs.
        setPhotosInfo(ownPhotos(data.pics).some(pic => pic && !pic.startsWith('blob:')));
        console.log("Profile pictures fetched:", data);
      } else {
        setPhotosInfo(false); // Explicitly set to falsy if fetch fails
        throw new Error(`Failed to fetch profile pictures: ${response.status}`);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        setPhotosInfo(false); // Explicitly set to falsy on error
        console.error("Error fetching profile pictures:", error);
      }
    }
  };

  useEffect(() => {
    if (user?.userId) {
      fetchProfilePictures();
    }
    return () => abortControllerRef.current?.abort();
    // eslint-disable-next-line
  }, [user?.userId, uploadTrigger]);

  // A photo only counts as "done" once it's a real, uploaded URL — never while
  // it's still just the local blob: preview. This is what onboarding's
  // "Continue" completeness check reads, so it can no longer advance based on
  // an upload that hasn't actually finished (or that failed).
  useEffect(() => {
    setPhotosInfo(pictures.some(pic => pic && !pic.startsWith('blob:')));
  }, [pictures, setPhotosInfo]);

  const handleFileChange = async (index, event) => {
    const file = event.target.files[0];
    if (file) {
      await handleUpload(index, file);
    }
  };

  const handleDelete = async (index) => {
    uploadAbortControllersRef.current[index]?.abort();
    delete uploadAbortControllersRef.current[index];
    setUploadStatus(prev => { const next = { ...prev }; delete next[index]; return next; });
    setPendingFiles(prev => { const next = { ...prev }; delete next[index]; return next; });

    const updatedPictures = pictures.filter((_, i) => i !== index);
    setPictures(updatedPictures);
    await updateBackend(updatedPictures);
  };

  const handleUpload = async (index, file) => {
    if (!file) {
      console.error("No file selected for upload");
      return;
    }

    // Show local preview immediately so the thumbnail appears without waiting
    // for the server — but this is only a preview. It's explicitly excluded
    // from photosInfo (above) and from being sent to the backend (see the
    // updatePictureOrder effect below) until the real upload confirms it.
    const localUrl = URL.createObjectURL(file);
    setPictures(prev => {
      const next = [...prev];
      next[index] = localUrl;
      return next;
    });
    setUploadStatus(prev => ({ ...prev, [index]: 'uploading' }));
    setPendingFiles(prev => ({ ...prev, [index]: file }));

    const controller = new AbortController();
    uploadAbortControllersRef.current[index] = controller;

    try {
      const { idToken, csrfToken } = await getFreshTokens();

      const formData = new FormData();
      formData.append("userPic", file);
      formData.append("userId", user.userId);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/upload/profilePic`,
        {
          method: "POST",
          credentials: 'include',
          body: formData,
          headers: {
            "Authorization": `Bearer ${idToken}`,
            "X-CSRF-Token": csrfToken,
          },
          signal: controller.signal,
        }
      );

      if (response.status === 403) {
        await getFreshTokens();
        return handleUpload(index, file);
      }

      if (response.ok) {
        const data = await response.json();
        setPictures(ownPhotos(data.user.pics));
        URL.revokeObjectURL(localUrl);
        setUploadStatus(prev => ({ ...prev, [index]: 'success' }));
        setPendingFiles(prev => { const next = { ...prev }; delete next[index]; return next; });
        console.log("Upload successful:", data);
        // Success confirmation is transient — the thumbnail itself (now a
        // real URL) is the lasting signal that it's saved.
        setTimeout(() => {
          setUploadStatus(prev => (prev[index] === 'success' ? { ...prev, [index]: undefined } : prev));
        }, 2000);
      } else {
        throw new Error(`Upload failed: ${response.status}`);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        setUploadStatus(prev => ({ ...prev, [index]: 'error' }));
        console.error("Error uploading file:", error);
      }
    } finally {
      delete uploadAbortControllersRef.current[index];
    }
  };

  const handleRetry = (index) => {
    const file = pendingFiles[index];
    if (file) handleUpload(index, file);
  };

  const movePicture = (dragIndex, hoverIndex) => {
    setPictures((prevPictures) => {
      const newPictures = Array.from(prevPictures);
      const [draggedPicture] = newPictures.splice(dragIndex, 1);
      newPictures.splice(hoverIndex, 0, draggedPicture);
      return newPictures;
    });
  };

  const updatePictureOrder = async (updatedPictures) => {
    const controller = new AbortController();

    try {
      const { idToken, csrfToken } = await getFreshTokens();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/userpics`,
        {
          method: "PUT",
          credentials: 'include',
          body: JSON.stringify({
            userId: user.userId,
            pics: updatedPictures,
          }),
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
            "X-CSRF-Token": csrfToken,
          },
          signal: controller.signal,
        }
      );

      if (response.status === 403) {
        await getFreshTokens();
        return updatePictureOrder(updatedPictures);
      }

      if (response.status === 200) {
        console.log("Picture order updated successfully");
      } else {
        throw new Error(`Failed to update picture order: ${response.status}`);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error updating picture order:", error);
      }
    }
  };

  useEffect(() => {
    // Never sync while any slot is still a local blob: preview — this used to
    // be the exact path a blob: URL could leak into the database (this effect
    // firing on the optimistic preview state, before the real upload
    // resolved, PUTting the blob straight to the backend with no validation).
    const hasBlob = pictures.some(pic => pic && pic.startsWith('blob:'));
    if (!hasBlob && pictures.some((pic) => pic !== null)) {
      updatePictureOrder(pictures);
    }
  }, [pictures]);

  const updateBackend = async (updatedPictures) => {
    const controller = new AbortController();

    try {
      const { idToken, csrfToken } = await getFreshTokens();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/userpics/`,
        {
          method: "PUT",
          credentials: 'include',
          body: JSON.stringify({
            userId: user.userId,
            pics: updatedPictures,
          }),
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
            "X-CSRF-Token": csrfToken,
          },
          signal: controller.signal,
        }
      );

      if (response.status === 403) {
        await getFreshTokens();
        return updateBackend(updatedPictures);
      }

      if (response.status === 200) {
        console.log("Pictures updated successfully");
        // Lets Messenger refresh their own picture (MessengerContext).
        window.dispatchEvent(new Event('ic:pics-updated'));
      } else {
        throw new Error(`Failed to update pictures: ${response.status}`);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error updating pictures:", error);
      }
    }
  };

  const filledPictures = [...pictures];
  while (filledPictures.length < maxPhotos) {
    filledPictures.push(null);
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="photo-upload-container">
        {filledPictures.map((picture, index) => (
          <DraggablePicture
            key={index}
            index={index}
            picture={picture}
            movePicture={movePicture}
            handleFileChange={handleFileChange}
            handleDelete={handleDelete}
            status={uploadStatus[index]}
            onRetry={() => handleRetry(index)}
          />
        ))}
      </div>
      {/* Some apps' built-in browsers (Facebook, Instagram…) won't open the
          photo picker at all — offer the phone's own browser. */}
      {isInAppBrowser() && (
        <p className="photo-inapp-hint">
          Photo picker not opening? That can happen inside {inAppName() || 'this app'}.{' '}
          {canJumpToBrowser()
            ? <button type="button" className="photo-inapp-link" onClick={() => openInExternalBrowser()}>Open this page in your browser</button>
            : <>Tap ••• and choose “Open in browser”.</>}
        </p>
      )}
    </DndProvider>
  );
};

export default ProfilePicturesGrid;


// import React, { useState, useEffect, useRef } from "react";
// import { DndProvider, useDrag, useDrop } from "react-dnd";
// import { HTML5Backend } from "react-dnd-html5-backend";
// import "./ProfilePhotoUpload.css";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faTimes } from "@fortawesome/free-solid-svg-icons";
// import { useAuthContext } from "../../hooks/useAuthContext";
// import { initializeApp, getApps } from "firebase/app";
// import { getAuth } from "firebase/auth";
// import firebase from "../../Firebase";
// import Cookies from "js-cookie";

// if (!getApps().length) {
//   initializeApp(firebase);
// }
 
// const DND_ITEM_TYPE = "picture";

// const DraggablePicture = ({
//   picture,
//   index,
//   movePicture,
//   handleFileChange,
//   handleDelete,
// }) => {
//   const [, drag] = useDrag(
//     () => ({
//       type: DND_ITEM_TYPE,
//       item: { index },
//     }),
//     [index]
//   );

//   const [, drop] = useDrop(
//     () => ({
//       accept: DND_ITEM_TYPE,
//       hover(item) {
//         const dragIndex = item.index;
//         const hoverIndex = index;
//         if (dragIndex === hoverIndex) {
//           return;
//         }
//         movePicture(dragIndex, hoverIndex);
//         item.index = hoverIndex;
//       },
//     }),
//     [index, movePicture]
//   );

//   return (
//     <div ref={(node) => drag(drop(node))} className="photo-upload-square">
//       {picture && (
//         <div className="delete-icon" onClick={() => handleDelete(index)}>
//           <FontAwesomeIcon icon={faTimes} />
//         </div>
//       )}

//       {picture ? (
//         <img
//           src={picture}
//           alt={`Profile ${index + 1}`}
//           className="profile-photo-preview"
//         />
//       ) : (
//         <>
//           <input
//             type="file"
//             id={`file-input-${index}`}
//             className="file-input"
//             onChange={(event) => handleFileChange(index, event)}
//             style={{ display: "none" }}
//           />
//           <label htmlFor={`file-input-${index}`} className="file-label">
//             <div className="upload-placeholder">+</div>
//           </label>
//         </>
//       )}
//     </div>
//   );
// };

// const ProfilePicturesGrid = ({ setPhotosInfo }) => {
//   const [pictures, setPictures] = useState(Array(5).fill(null));
//   const [uploadTrigger, setUploadTrigger] = useState(0);
//   const maxPhotos = 5;
//   const { user } = useAuthContext();
//   const abortControllerRef = useRef(null);

//   const getFreshTokens = async () => {
//     try {
//       const idToken = await firebase.auth().currentUser.getIdToken(true);
//       const csrfToken = Cookies.get("mytoken");
//       return { idToken, csrfToken };
//     } catch (error) {
//       console.error("Token refresh failed:", error);
//       window.location.href = '/login';
//       throw error;
//     }
//   };

//   const fetchProfilePictures = async () => {
//     if (!user?.userId) return;

//     const controller = new AbortController();
//     abortControllerRef.current = controller;

//     try {
//       const { idToken, csrfToken } = await getFreshTokens();

//       const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/userpics`,
//         {
//           method: "POST",
//           credentials: 'include',
//           body: JSON.stringify({
//             userId: user.userId,
//             _csrf: csrfToken,
//           }),
//           headers: {
//             "Content-Type": "application/json",
//             "Authorization": `Bearer ${idToken}`,
//             "X-CSRF-Token": csrfToken,
//           },
//           signal: controller.signal,
//         }
//       );

//       if (response.status === 403) {
//         await getFreshTokens();
//         return fetchProfilePictures();
//       }

//       if (response.status === 200) {
//         const data = await response.json();
//         setPictures(data.pics);
//         console.log("Profile pictures fetched:", data);
//       } else {
//         throw new Error(`Failed to fetch profile pictures: ${response.status}`);
//       }
//     } catch (error) {
//       if (error.name !== 'AbortError') {
//         console.error("Error fetching profile pictures:", error);
//       }
//     }
//   };

//   useEffect(() => {
//     if (user?.userId) {
//       fetchProfilePictures();
//     }
//     return () => abortControllerRef.current?.abort();
//   }, [user?.userId, uploadTrigger]);

//   const handleFileChange = async (index, event) => {
//     const file = event.target.files[0];
//     if (file) {
//       await handleUpload(index, file);
//     }
//   };

//   const handleDelete = async (index) => {
//     const updatedPictures = pictures.filter((_, i) => i !== index);
//     setPictures(updatedPictures);
//     await updateBackend(updatedPictures);
//   };

//   const handleUpload = async (index, file) => {
//     if (!file) {
//       console.error("No file selected for upload");
//       return;
//     }

//     const controller = new AbortController();
    
//     try {
//       const { idToken, csrfToken } = await getFreshTokens();

//       const formData = new FormData();
//       formData.append("userPic", file);
//       formData.append("userId", user.userId);

//       const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/upload/profilePic`,
//         {
//           method: "POST",
//           credentials: 'include',
//           body: formData,
//           headers: {
//             "Authorization": `Bearer ${idToken}`,
//             "X-CSRF-Token": csrfToken,
//           },
//           signal: controller.signal,
//         }
//       );

//       if (response.status === 403) {
//         await getFreshTokens();
//         return handleUpload(index, file);
//       }

//       if (response.ok) {
//         const data = await response.json();
//         console.log("Upload successful:", data);
//         setPictures(data.user.pics);
//       } else {
//         throw new Error(`Upload failed: ${response.status}`);
//       }
//     } catch (error) {
//       if (error.name !== 'AbortError') {
//         console.error("Error uploading file:", error);
//       }
//     }
//   };

//   const movePicture = (dragIndex, hoverIndex) => {
//     setPictures((prevPictures) => {
//       const newPictures = Array.from(prevPictures);
//       const [draggedPicture] = newPictures.splice(dragIndex, 1);
//       newPictures.splice(hoverIndex, 0, draggedPicture);
//       return newPictures;
//     });
//   };

//   const updatePictureOrder = async (updatedPictures) => {
//     const controller = new AbortController();

//     try {
//       const { idToken, csrfToken } = await getFreshTokens();

//       const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/userpics`,
//         {
//           method: "PUT",
//           credentials: 'include',
//           body: JSON.stringify({
//             userId: user.userId,
//             pics: updatedPictures,
//           }),
//           headers: {
//             "Content-Type": "application/json",
//             "Authorization": `Bearer ${idToken}`,
//             "X-CSRF-Token": csrfToken,
//           },
//           signal: controller.signal,
//         }
//       );

//       if (response.status === 403) {
//         await getFreshTokens();
//         return updatePictureOrder(updatedPictures);
//       }

//       if (response.status === 200) {
//         console.log("Picture order updated successfully");
//       } else {
//         throw new Error(`Failed to update picture order: ${response.status}`);
//       }
//     } catch (error) {
//       if (error.name !== 'AbortError') {
//         console.error("Error updating picture order:", error);
//       }
//     }
//   };

//   useEffect(() => {
//     if (pictures.some((pic) => pic !== null)) {
//       updatePictureOrder(pictures);
//     }
//   }, [pictures]);

//   const updateBackend = async (updatedPictures) => {
//     const controller = new AbortController();

//     try {
//       const { idToken, csrfToken } = await getFreshTokens();

//       const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/userpics/`,
//         {
//           method: "PUT",
//           credentials: 'include',
//           body: JSON.stringify({
//             userId: user.userId,
//             pics: updatedPictures,
//           }),
//           headers: {
//             "Content-Type": "application/json",
//             "Authorization": `Bearer ${idToken}`,
//             "X-CSRF-Token": csrfToken,
//           },
//           signal: controller.signal,
//         }
//       );

//       if (response.status === 403) {
//         await getFreshTokens();
//         return updateBackend(updatedPictures);
//       }

//       if (response.status === 200) {
//         console.log("Pictures updated successfully");
//       } else {
//         throw new Error(`Failed to update pictures: ${response.status}`);
//       }
//     } catch (error) {
//       if (error.name !== 'AbortError') {
//         console.error("Error updating pictures:", error);
//       }
//     }
//   };

//   const filledPictures = [...pictures];
//   while (filledPictures.length < maxPhotos) {
//     filledPictures.push(null);
//   }

//   return (
//     <DndProvider backend={HTML5Backend}>
//       <div className="photo-upload-container">
//         {filledPictures.map((picture, index) => (
//           <DraggablePicture
//             key={index}
//             index={index}
//             picture={picture}
//             movePicture={movePicture}
//             handleFileChange={handleFileChange}
//             handleDelete={handleDelete}
//           />
//         ))}
//       </div>
//     </DndProvider>
//   );
// };

// export default ProfilePicturesGrid;


//2025
// 
// // import React, { useState, useEffect } from "react";
// import { DndProvider, useDrag, useDrop } from "react-dnd";
// import { HTML5Backend } from "react-dnd-html5-backend";
// import "./ProfilePhotoUpload.css";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faTimes } from "@fortawesome/free-solid-svg-icons";
// import { useAuthContext } from "../../hooks/useAuthContext";
// import { initializeApp, getApps } from "firebase/app";
// import { getAuth } from "firebase/auth";
// import firebase from "../../Firebase";
// import Cookies from "js-cookie";

// if (!getApps().length) {
//   initializeApp(firebase);
// }

// const DND_ITEM_TYPE = "picture";
// // DraggablePicture component allows each picture to be draggable within the grid
// const DraggablePicture = ({
//   picture,
//   index,
//   movePicture,
//   handleFileChange,
//   handleDelete,
// }) => {
//   const [, drag] = useDrag(
//     () => ({
//       type: DND_ITEM_TYPE,
//       item: { index },
//     }),
//     [index]
//   );

//   const [, drop] = useDrop(
//     () => ({
//       accept: DND_ITEM_TYPE,
//       hover(item) {
//         const dragIndex = item.index;
//         const hoverIndex = index;
//         if (dragIndex === hoverIndex) {
//           return;
//         }
//         movePicture(dragIndex, hoverIndex);
//         item.index = hoverIndex;
//       },
//     }),
//     [index, movePicture]
//   );

//   return (
//     <div ref={(node) => drag(drop(node))} className="photo-upload-square">
//       {picture && (
//         <div className="delete-icon" onClick={() => handleDelete(index)}>
//           <FontAwesomeIcon icon={faTimes} />
//         </div>
//       )}

//       {picture ? (
//         <img
//           src={picture}
//           alt={`Profile ${index + 1}`}
//           className="profile-photo-preview"
//         />
//       ) : (
//         <>
//           <input
//             type="file"
//             id={`file-input-${index}`}
//             className="file-input"
//             onChange={(event) => handleFileChange(index, event)}
//             style={{ display: "none" }} // Hide the input element
//           />
//           <label htmlFor={`file-input-${index}`} className="file-label">
//             <div className="upload-placeholder">+</div>
//           </label>
//         </>
//       )}
//     </div>
//   );
// };

// const ProfilePicturesGrid = ({ onImageLinksUpdate }) => {
//   const [pictures, setPictures] = useState(Array(5).fill(null));
//   const [uploadTrigger, setUploadTrigger] = useState(0);
//   const maxPhotos = 5;

//   const { user } = useAuthContext();

//   const [token, setToken] = useState(null);
//   const [csrfToken, setCsrfToken] = useState(null); // New state variable for CSRF token

//   useEffect(() => {
//     const fetchCsrfToken = async () => {
//       // Fetch the CSRF token from the "mytoken" cookie
//       const csrfToken1 = Cookies.get("mytoken");
//       setCsrfToken(csrfToken1);
//     };

//     fetchCsrfToken();
//   }, []);

//   useEffect(() => {
//     const fetchToken = async () => {
//       const token = await firebase.auth().currentUser.getIdToken(true);
//       setToken(token);
//     };

//     console.log('csrfToken',csrfToken)

//     fetchToken();
//   }, []); // Empty dependency array means this effect runs once on component mount

//   useEffect(() => {
//     const fetchProfilePictures = async () => {
//       if (!user || !user.userId) {
//         console.error("User ID is not available.");
//         return;
//       }

//       try {
//         console.log(
//           "Sending POST request to fetch profile pictures and csrfToken is",
//           csrfToken
//         );
//         const response = await fetch(
//           `${process.env.REACT_APP_API_BASE_URL}/userpics`,
//           {
//             method: "POST",
//             body: JSON.stringify({
//               userId: user.userId,
//               _csrf: csrfToken, // Include the CSRF token in the body of the request
//             }),
//             headers: {
//               "Content-Type": "application/json",
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );
//         if (response.status === 200) {
//           const data = await response.json();
//           setPictures(data.pics);
//           console.log("response pictures back:", data);
//         } else {
//           throw new Error("Failed to fetch profile pictures");
//         }
//       } catch (error) {
//         console.error("Error fetching profile pictures:", error);
//       }
//     };

//     if (token) {
//       // Only fetch profile pictures if token is not null
//       fetchProfilePictures();
//     }
//   }, [user.userId, uploadTrigger, token, csrfToken]); // Add csrfToken to dependency array

//   const handleFileChange = async (index, event) => {
//     const file = event.target.files[0];
//     if (file) {
//       // Pass the file directly to handleUpload
//       await handleUpload(index, file);
//     }
//   };

//   const handleDelete = async (index) => {
//     const updatedPictures = pictures.filter((_, i) => i !== index);
//     setPictures(updatedPictures);
//     await updateBackend(updatedPictures); // Function to update the backend
//   };

//   const handleUpload = async (index, file) => {
//     // console.log("csrfToken1", csrfToken);

//     if (!file ) {
//       console.error("No file selected for upload or no csrfToken.");
      
//       return;
//     }

//     // Create a new FormData instance
//     const formData = new FormData();

//     // Append the file and user ID to the form data
//     formData.append("userPic", file);
//     formData.append("userId", user.userId);

//     // Define the headers
//     const requestHeaders = {
//       Authorization: `Bearer ${token}`,
//       "CSRF-Token": csrfToken, // Include the CSRF token in the request headers
//     };

//     try {
//       const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/upload/profilePic`,
//         {
//           method: "POST",
//           body: formData, // Send the form data
//           headers: requestHeaders,
//         }
//       );

//       if (response.ok) {
//         const data = await response.json();
//         console.log("Response from POST request:", data);
//         // Update the pictures state with the new picture URLs
//         setPictures(data.user.pics);
//       } else {
//         console.error("Error from server:", response.statusText);
//       }
//     } catch (error) {
//       console.error("Error uploading file:", error);
//     }
//   };

//   const movePicture = (dragIndex, hoverIndex) => {
//     setPictures((prevPictures) => {
//       const newPictures = Array.from(prevPictures);
//       const [draggedPicture] = newPictures.splice(dragIndex, 1);
//       newPictures.splice(hoverIndex, 0, draggedPicture);
//       return newPictures;
//     });
//   };

//   // useEffect(() => {
//   //   if (!csrfToken) {
//   //     console.error("No csrfToken.");
//   //     return;
//   //   }
//   // });

//   // useEffect(() => {
//   //   if (!csrfToken) {
//   //     console.error("No csrfToken.");
//   //     return;
//   //   }
//   // });

//   useEffect(() => {
//     const updatePictureOrder = async () => {
//       // if (!csrfToken || pictures.every((pic) => pic === null)) {
//       //   console.error("No csrfToken or pictures to update.");
//       //   return;
//       // }
  
//       const formData = new FormData();
//       formData.append("userId", user.userId);
//       formData.append("pics", JSON.stringify(pictures)); // Since FormData doesn't support arrays directly, stringify it
//       formData.append("_csrf", csrfToken);
  
//       try {
//         const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userpics`, {
//           method: "PUT",
//           body: formData, // Send the FormData
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//           credentials: "include", // Include cookies with the request
//         });
  
//         if (response.status === 200) {
//           console.log("Picture order updated successfully");
//         } else {
//           throw new Error("Failed to update picture order");
//         }
//       } catch (error) {
//         console.error("Error updating picture order:", error);
//       }
//     };
  
//     if (pictures.some((pic) => pic !== null)) {
//       updatePictureOrder();
//     }
//   }, [pictures, token, csrfToken]);
  
//   const updateBackend = async (updatedPictures) => {
//     if (!csrfToken) {
//       console.error("No csrfToken.");
//       return;
//     }
  
//     const formData = new FormData();
//     formData.append("userId", user.userId);
//     formData.append("pics", JSON.stringify(updatedPictures)); // Since FormData doesn't support arrays directly, stringify it
//     formData.append("_csrf", csrfToken);
  
//     try {
//       console.log("Sending PUT request to update pictures");
//       const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userpics/`, {
//         method: "PUT",
//         body: formData, // Send the FormData
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//         credentials: "include", // Include cookies with the request
//       });
  
//       if (response.status === 200) {
//         console.log("Pictures updated successfully");
//       } else {
//         throw new Error("Failed to update pictures");
//       }
//     } catch (error) {
//       console.error("Error updating pictures:", error);
//     }
//   };
//   // Ensure that the pictures array always has 5 elements
//   const filledPictures = [...pictures];
//   while (filledPictures.length < maxPhotos) {
//     filledPictures.push(null);
//   }

//   return (
//     <DndProvider backend={HTML5Backend}>
//       {/* <div className="photo-upload-wrapper"> */}
//       {/* <h2>My Photos</h2> */}
//       <div className="photo-upload-container">
//         {filledPictures.map((picture, index) => (
//           <DraggablePicture
//             key={index} // For simplicity, using index as key here; consider using unique IDs for production
//             index={index}
//             picture={picture}
//             movePicture={movePicture}
//             handleFileChange={handleFileChange}
//             handleDelete={handleDelete}
//           />
//         ))}
//       </div>
//       {/* </div> */}
//     </DndProvider>
//   );
// };

// export default ProfilePicturesGrid;
